/**
 * The patient app's single API client and query cache (MASTER_PLAN M2.9 / M2.10).
 *
 * Both live here rather than in `main.tsx` because they refer to each other: a `401`
 * from any endpoint has to put the session cache back to "anonymous", and the session
 * cache is what every guard and the shell read. Creating them together in one module
 * keeps that one wire visible instead of threading a setter through the tree.
 *
 * ## Why a 401 does not navigate from here
 *
 * It would need the router, which is created later, and it would mean two things could
 * move the patient: this callback and the route guard. Instead the callback records the
 * fact - the session is gone - and `RequireAuth` (the component that knows which route
 * the patient is on, and therefore what `?next=` should be) does the navigating.
 */

import { QueryClient } from '@tanstack/react-query';

import { createApiClient, type ApiClient } from '@platform/api-client';
import { ANONYMOUS_SESSION, SESSION_QUERY_KEY } from '@platform/api-client/react';

/**
 * No persister is installed and none may be: TanStack Query's cache stays in memory
 * only, because anything written to localStorage or IndexedDB would put PHI in the
 * browser's storage (ADR-007, SECURITY.md §7).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

export const apiClient: ApiClient = createApiClient({
  /*
   * Same origin. In development the Vite dev server proxies `/api` to the API with
   * `changeOrigin: false`, so the clinic host the browser used - and therefore the
   * tenant - survives the hop (see Docs/PATIENT_AUTH_UI.md).
   */
  baseUrl: '',

  onUnauthenticated: () => {
    /*
     * Seed rather than invalidate: refetching `GET /auth/session` would answer
     * `authenticated: false` anyway, and doing it optimistically means the guard
     * redirects on the same tick instead of after a round trip.
     */
    queryClient.setQueryData(SESSION_QUERY_KEY, ANONYMOUS_SESSION);
  },

  onStepUpRequired: () => {
    /*
     * Patient MFA is optional and has no screen yet (SECURITY.md §4.1; the enrolment
     * and step-up surfaces are M11.9). Until it exists the call site shows the API's
     * own message, which tells the patient what to do, and this records the fact for
     * the browser console rather than silently swallowing it.
     */
    console.warn('The API asked for a second factor; the patient app has no step-up screen yet.');
  },
});
